import { inject } from '../../../../../../../common/container/inject';
import { saveFileInBrowser } from '../../../../../../../common/utils/saveFileInBrowser';
import { LayoutNotificationType } from '../../../../../../../view/layout/common/notification/store/ILayoutNotification';
import { IMainLayoutDomainStore, MainLayoutDomainStoreToken } from '../../../../../../../view/layout/main/store/domain/IMainLayoutDomainStore';
import { ILocalization, ILocalizationToken } from '../../../../../application/language/ILocalization';
import { ILearningRootService, LearningRootServiceToken } from '../../../../../service/LearningRootService';
import { ContentExportUI } from './ContentExportUI';
import { IContentExportDomain } from './IContentExportDomain';
import { IUnitWithCheckbox } from './IContentExportUI';
import JSZip from 'jszip'; 

export class ContentExportDomain implements IContentExportDomain {
  constructor(
    public ui = new ContentExportUI(),
    private learningRootService = inject<ILearningRootService>(LearningRootServiceToken),
    public layoutDomain: IMainLayoutDomainStore = inject<IMainLayoutDomainStore>(MainLayoutDomainStoreToken),
    private i18n: ILocalization = inject<ILocalization>(ILocalizationToken),
  ) {}

  boot = async () => {
    this.ui.isLoading.setEntity(true);
    const unitsListResult = await this.learningRootService.unit.getListCards({ limit: 100000 });
    this.ui.units.setList(unitsListResult);
    this.ui.filteredUnits.setList(unitsListResult);
    this.ui.isLoading.setEntity(false);
  };

  toggleSelecteAll = (check: boolean) => {
    this.ui.isSelectedAll.setEntity(check);
    if (this.ui.isSelectedAll.entity) {
      const prepareForExport: IUnitWithCheckbox[] = [];
      this.ui.units.list.forEach((unit) => {
        prepareForExport.push({ unitId: unit.id, isChecked: true });
      });
      this.ui.unitsForExporting.setList(prepareForExport);
    } else {
      this.ui.unitsForExporting.setList([]);
    }
  };

  toggleSelectUnit = (unitId: string, checked: boolean) => {
    const current = this.ui.unitsForExporting.list;
    if (checked) {
      if (!current.some((u) => u.unitId === unitId)) {
        this.ui.unitsForExporting.setList([...current, { unitId, isChecked: true }]);
      }
    } else {
      const updated = current.filter((u) => u.unitId !== unitId);
      this.ui.unitsForExporting.setList(updated);
    }
    const totalUnits = this.ui.units.list.length;
    const selectedUnits = this.ui.unitsForExporting.list.length;
    this.ui.isSelectedAll.setEntity(totalUnits > 0 && selectedUnits === totalUnits);
  };

  openPasswordDialog = () => {
    this.ui.exportPassword.setEntity('');
    this.ui.validationErrors.setList([]);          
    this.ui.isPasswordDialogOpen.setEntity(true);
  };

  closePasswordDialog = () => {
    this.ui.isPasswordDialogOpen.setEntity(false);
  };

  confirmPasswordAndExport = async () => {
    const password = this.ui.exportPassword.entity;
    await this.exportSelectedUnits(password);      
  };


exportSelectedUnits = async (password: string) => {
  if (this.ui.isExporting.entity) return;

  const unitsToExport = [...this.ui.unitsForExporting.list];
  if (unitsToExport.length === 0) return;

  this.ui.isExporting.setEntity(true);
  this.ui.exportProgress.setEntity({ current: 0, total: unitsToExport.length });

  const zip = new JSZip();
  const dateStr = this.getDateTimeString();



  try {
    zip.file('encryptKey.txt', password);

    for (let i = 0; i < unitsToExport.length; i++) {
      const { unitId } = unitsToExport[i];

      try {
        const blob = await this.learningRootService.contentTransfer.getUnitExport(unitId, password);

        const arrayBuffer = await blob.arrayBuffer();
        const unit = this.ui.units.list.find((u) => u.id === unitId);
        const unitTitle = unit?.settings?.titleHeading ?? unitId;

        const safeTitle = this.sanitizeFilename(unitTitle);
        zip.file(`${safeTitle}_${dateStr}.zip`, arrayBuffer);

        console.log(`[EXPORT SUCCESS] ${unitId}`);
      } catch (err: any) {
        throw err;
      } finally {
        this.ui.exportProgress.setEntity({ current: i + 1, total: unitsToExport.length });
      }
    }

    this.ui.isPasswordDialogOpen.setEntity(false);

    const finalZipBlob = await zip.generateAsync({ type: 'blob' });
    saveFileInBrowser(`export_units_${dateStr}.zip`, finalZipBlob);

    this.ui.unitsForExporting.setList([]);
    this.ui.isSelectedAll.setEntity(false);

    this.layoutDomain.notifications.showNotification({
      type: LayoutNotificationType.success,
      text: this.i18n.translate('contentTransfer.exportPage.notificationExport'),
    });

  } catch (err: any) {
    const msgs: string[] = Array.isArray(err?.messages)
      ? err.messages
      : [err?.message || ''];

    const translateErrors = msgs.map((key) => {
      switch (key) {
        case 'error.VALIDATION_MESSAGE.minLength':
          return this.i18n.translate('contentTransfer.exportPage.error.VALIDATION_MESSAGE.minLength');
        case 'error.VALIDATION_MESSAGE.maxLength':
          return this.i18n.translate('contentTransfer.exportPage.error.VALIDATION_MESSAGE.maxLength');
        case 'error.VALIDATION_MESSAGE.weakPassword':
          return this.i18n.translate('contentTransfer.exportPage.error.VALIDATION_MESSAGE.weakPassword');
        default:
          return key;
      }
    });

    this.ui.validationErrors.setList(translateErrors.filter(Boolean));

    this.ui.isPasswordDialogOpen.setEntity(true);

  } finally {
    this.ui.isExporting.setEntity(false);
  }
};

private sanitizeFilename = (name: string) => {
  return name
    .replace(/[:/\\?<>|*"']/g, '_')
    .replace(/\s+/g, ' ')
    .trim();
};


private getDateTimeString(): string {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}-${pad(now.getMinutes())}`;
  return `${date}_${time}`;
}

  
  private saveUnitZip = async (unitId: string, blob: Blob) => {
    const unit = this.ui.units.list.find((u) => u.id === unitId);
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const date = `${day}.${month}.${year}`;
    saveFileInBrowser(`${unit?.settings?.titleHeading ?? unitId}_${date}.zip`, blob);
  };

  handleSearchByGroupsAndName = (query: string) => {
    const normalized = query.toLowerCase().trim();
    this.ui.searchQuery.setEntity(normalized);

    if (!normalized) {
      this.ui.filteredUnits.setList(this.ui.units.list);
      return;
    }

    const filtered = this.ui.units.list.filter((unit) => {
      const title = unit.settings?.titleHeading?.toLowerCase() ?? '';
      return title.includes(normalized);
    });

    this.ui.filteredUnits.setList(filtered);
  };
  handlePasswordChange = (raw: string) => {
    const allowed = raw.replace(/\s+/g, '');
    this.ui.exportPassword.setEntity(allowed || '');
    this.ui.validationErrors.setList([]);           
  };
  togglePasswordVisibility = () => {
    this.ui.isPasswordVisible.setEntity(!this.ui.isPasswordVisible.entity)
  }
}
